import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import customFetch from '../utils/customFetch';

export type ServiceAuthToken = {
    id: string;
    created_time: string;
    updated_time: string;
    user_id: string;
    service_id: string;
    expiry: string;
    token_type: string;
    scope: string;
    service_unique_id: string;
    service_readable_info: string;
    token_name: string;
}

export type BotAuthToken = {
    id: string;
    created_time: string;
    updated_time: string;
    user_id: string;
    service_id: string;
    expiry: string;
    token_type: string;
    scope: string;
    service_unique_id: string;
    service_readable_info: string;
}

export const useGetAllServiceAuthTokens = () => {
    return useQuery<ServiceAuthToken[], ErrorEvent>({
      queryKey: ['serviceAuthTokens'],
      queryFn: () => {
        return customFetch(`/v1/authTokens/service`)
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error('Failed to get service auth tokens'))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}

export const useGetServiceAuthTokensForService = (serviceId: string) => {
    return useQuery<ServiceAuthToken[], ErrorEvent>({
      queryKey: ['serviceAuthTokens', serviceId],
      queryFn: () => {
        return customFetch(`/v1/authTokens/service?serviceId=${serviceId}`)
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error('Failed to get service auth tokens'))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}

export const useDeleteServiceAuthToken = () => {
  return useMutation<ServiceAuthToken[], ErrorEvent, string>({
    mutationFn: (tokenId) => {
      return customFetch(`/v1/authTokens/service/${tokenId}`, {
        method: 'DELETE'
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get service auth tokens'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const useRenameServiceAuthToken = () => {
  return useMutation<ServiceAuthToken[], ErrorEvent,
      {tokenId: string, newName: string}>({
    mutationFn: ({tokenId, newName}) => {
      return customFetch(`/v1/authTokens/service/${tokenId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token_name: newName
        })
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to rename service auth token'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const updateNodeToken = async ({
    nodeId,
    serviceAuthTokenId
    }: {
        nodeId: string;
        serviceAuthTokenId: string | null;
    }) => {
    const response = await customFetch(`/v1/nodes/${nodeId}/token`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            service_auth_token_id: serviceAuthTokenId,
        }),
    });
    
    if (!response.ok) throw new Error('Failed to update node token');
    return response.json();
};